/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { all, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { fetchWrapperFuncNoLoading } from '../../../utils';
import { getPlaylistAdminApi, getSystemConfig } from '../../System/systemConfig';
import {
    PLAYLIST_BACKWARD,
    PLAYLIST_FAST_BACKWARD,
    PLAYLIST_FAST_FORWARD,
    PLAYLIST_FORWARD,
    PLAYLIST_IMPORT_MUSICBRAINZ_SONG,
    PLAYLIST_INITIATE_SEARCH,
    searchPlaylist,
    SEARCH_PLAYLIST,
    setPlaylistSearch,
    setPlaylistSearchCount,
    setPlaylistSearchCurrentPos,
    setPlaylistSearchList
} from './actions';
import {
    playlistSearchCount,
    playlistSearchCurrentPos,
    playlistSearchFromDate,
    playlistSearchPageSize,
    playlistSearchString,
    playlistSearchToDate
} from './selectors';

const playlistApi = getPlaylistAdminApi();

function* searchPlaylistGenerator(): any {
    let startPos = yield select(playlistSearchCurrentPos);
    let limit = yield select(playlistSearchPageSize);
    let from = yield select(playlistSearchFromDate);
    let to = yield select(playlistSearchToDate);
    let q = yield select(playlistSearchString);
    yield* fetchWrapperFuncNoLoading(() => playlistApi.searchPlaylist(getSystemConfig().playlistStationId, startPos, limit, from, to, q), [
        r => setPlaylistSearchList(r)
    ]);

    yield* fetchWrapperFuncNoLoading(() => playlistApi.countPlayList(getSystemConfig().playlistStationId, from, to, q), [
        r => setPlaylistSearchCount(r)
    ]);
}

function* playListForwardGenerator(): any {
    const pageSize = yield select(playlistSearchPageSize);
    const count = yield select(playlistSearchCount);
    const currentPos = yield select(playlistSearchCurrentPos);

    if (currentPos + pageSize <= count) {
        yield put(setPlaylistSearchCurrentPos(currentPos + pageSize));
        yield put(searchPlaylist());
    }
}

function* playListBackwardGenerator() {
    const pageSize: number = yield select(playlistSearchPageSize);
    const currentPos: number = yield select(playlistSearchCurrentPos);

    if (currentPos > 0) {
        const nextPos = currentPos > pageSize ? currentPos - pageSize : 0;
        yield put(setPlaylistSearchCurrentPos(nextPos));
        yield put(searchPlaylist());
    }
}
function* playListFastBackwardGenerator() {
    const currentPos: number = yield select(playlistSearchCurrentPos);
    if (currentPos > 0) {
        yield put(setPlaylistSearchCurrentPos(0));
        yield put(searchPlaylist());
    }
}
function* playListFastForwardGenerator() {
    const pageSize: number = yield select(playlistSearchPageSize);
    const count: number = yield select(playlistSearchCount);
    const currentPos: number = yield select(playlistSearchCurrentPos);

    const nextPos = count - pageSize > 0 ? count - pageSize : 0;
    if (currentPos !== nextPos) {
        yield put(setPlaylistSearchCurrentPos(nextPos));
        yield put(searchPlaylist());
    }
}

function* importMusicbrainzSong(action: Action<string>) {
    yield* fetchWrapperFuncNoLoading(() => playlistApi.importMusicbrainzSong(action.payload), [r => searchPlaylist()]);
}

function* initiateSearch(action: Action<string>) {
    yield put(setPlaylistSearchCurrentPos(0));
    yield put(setPlaylistSearch(action.payload));
    yield put(searchPlaylist());
}

function* watchPlaylistSearchActions() {
    yield takeLatest(PLAYLIST_FORWARD, playListForwardGenerator);
    yield takeLatest(PLAYLIST_BACKWARD, playListBackwardGenerator);
    yield takeLatest(PLAYLIST_FAST_FORWARD, playListFastForwardGenerator);
    yield takeLatest(PLAYLIST_FAST_BACKWARD, playListFastBackwardGenerator);
    yield takeLatest(SEARCH_PLAYLIST, searchPlaylistGenerator);
    yield takeLatest(PLAYLIST_INITIATE_SEARCH, initiateSearch);
    yield takeEvery(PLAYLIST_IMPORT_MUSICBRAINZ_SONG, importMusicbrainzSong);
}

export function* playlistSearchSagas() {
    yield all([watchPlaylistSearchActions()]);
}
